import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { gbp, num } from "@/lib/format";
import { Plus, Pencil, Trash2 } from "lucide-react";

const SAVINGS_BLANK = { period: "", site: "", gainshare_pct: 0, weeks_in_period: 0, opening_balance: 0, savings_in_period: 0, closing_balance: 0 };

function useCrud(key, endpoint) {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: [key], queryFn: () => api.get(endpoint).then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: [key] }); qc.invalidateQueries({ queryKey: ["stats"] }); };
  return { data, isLoading, refresh };
}

export default function SavingsServiceCreditsView() {
  return (
    <div>
      <PageHeader title="Savings & Service Credits" subtitle="Track period savings (gainshare) and SLA service credits." />
      <Tabs defaultValue="savings">
        <TabsList className="mb-4">
          <TabsTrigger value="savings" data-testid="tab-savings">Savings Tracker</TabsTrigger>
          <TabsTrigger value="pulled" data-testid="tab-pulled-forward">Pulled Forward Savings</TabsTrigger>
          <TabsTrigger value="credits" data-testid="tab-credits">Service Credits</TabsTrigger>
        </TabsList>
        <TabsContent value="savings"><SavingsTab /></TabsContent>
        <TabsContent value="pulled"><PulledForwardTab /></TabsContent>
        <TabsContent value="credits"><CreditsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SavingsTab() {
  const { data, isLoading, refresh } = useCrud("savings", "/savings");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(SAVINGS_BLANK);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [siteFilter, setSiteFilter] = useState("all");

  const siteOptions = useMemo(() => [...new Set(data.map((d) => d.site).filter(Boolean))].sort(), [data]);
  const rows = useMemo(() => (siteFilter === "all" ? data : data.filter((r) => r.site === siteFilter)), [data, siteFilter]);

  const openAdd = () => { setEditing(null); setForm(SAVINGS_BLANK); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ period: r.period, site: r.site || "", gainshare_pct: r.gainshare_pct, weeks_in_period: r.weeks_in_period, opening_balance: r.opening_balance, savings_in_period: r.savings_in_period, closing_balance: r.closing_balance }); setDialogOpen(true); };

  const save = async () => {
    if (!form.period.trim()) { toast.error("Period is required"); return; }
    setSaving(true);
    try {
      const p = { ...form };
      ["gainshare_pct", "weeks_in_period", "opening_balance", "savings_in_period", "closing_balance"].forEach((k) => (p[k] = Number(p[k]) || 0));
      if (editing) await api.put(`/savings/${editing.id}`, p);
      else await api.post("/savings", p);
      toast.success(editing ? "Savings updated" : "Savings added");
      refresh(); setDialogOpen(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/savings/${deleteTarget.id}`); toast.success("Deleted"); refresh(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  const totalSavings = rows.reduce((a, b) => a + (b.savings_in_period || 0), 0);

  if (isLoading) return <Loading />;

  return (
    <div>
      <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="savings-site-filter"><SelectValue placeholder="Site" /></SelectTrigger>
          <SelectContent className="max-h-64"><SelectItem value="all">All sites</SelectItem>{siteOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={openAdd} className="bg-[#78BE20] hover:bg-[#5fa016]" data-testid="add-savings-record-button"><Plus className="w-4 h-4 mr-1.5" /> Add savings</Button>
      </div>
      <TableShell>
        <table className="w-full text-sm" data-testid="savings-tracker-table">
          <thead>
            <tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
              <th className="px-4 py-3">Site</th>
              <th className="px-4 py-3">Period</th>
              <th className="px-4 py-3 text-right">Gainshare %</th>
              <th className="px-4 py-3 text-right">Weeks</th>
              <th className="px-4 py-3 text-right">Opening Balance</th>
              <th className="px-4 py-3 text-right">Savings in Period</th>
              <th className="px-4 py-3 text-right">Closing Balance</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`savings-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300">{r.site || "—"}</td>
                <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">{r.period}</td>
                <td className="px-4 py-3 text-right font-mono-num">{num(r.gainshare_pct)}%</td>
                <td className="px-4 py-3 text-right font-mono-num">{r.weeks_in_period}</td>
                <td className="px-4 py-3 text-right font-mono-num">{gbp(r.opening_balance)}</td>
                <td className="px-4 py-3 text-right font-mono-num text-emerald-600">{gbp(r.savings_in_period)}</td>
                <td className="px-4 py-3 text-right font-mono-num">{gbp(r.closing_balance)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => setDeleteTarget(r)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot><tr className="bg-slate-50 dark:bg-slate-800/60 font-semibold"><td className="px-4 py-3" colSpan={5}>Total savings{siteFilter !== "all" ? ` — ${siteFilter}` : ""}</td><td className="px-4 py-3 text-right font-mono-num text-emerald-700">{gbp(totalSavings)}</td><td colSpan={2} /></tr></tfoot>
          )}
        </table>
        {rows.length === 0 && <EmptyState message="No savings records yet." />}
      </TableShell>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="savings-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit savings" : "Add savings"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Period *</Label><Input className="mt-1.5" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="PERIOD 0127" /></div>
            <div className="col-span-2"><Label>Site</Label><Input className="mt-1.5" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} placeholder="IPL Normanton" /></div>
            <div><Label>Gainshare %</Label><Input className="mt-1.5" type="number" value={form.gainshare_pct} onChange={(e) => setForm({ ...form, gainshare_pct: e.target.value })} /></div>
            <div><Label>Weeks in period</Label><Input className="mt-1.5" type="number" value={form.weeks_in_period} onChange={(e) => setForm({ ...form, weeks_in_period: e.target.value })} /></div>
            <div><Label>Opening balance (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} /></div>
            <div><Label>Savings in period (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form.savings_in_period} onChange={(e) => setForm({ ...form, savings_in_period: e.target.value })} /></div>
            <div className="col-span-2"><Label>Closing balance (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form.closing_balance} onChange={(e) => setForm({ ...form, closing_balance: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#78BE20] hover:bg-[#5fa016]">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete savings record?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PulledForwardTab() {
  const qc = useQueryClient();
  const [fy, setFy] = useState("FY27");
  const [site, setSite] = useState("IPL Normanton");
  const { data, isLoading } = useQuery({ queryKey: ["pf-worksheet", fy, site], queryFn: () => api.get("/pulled-forward/worksheet", { params: { fy, site } }).then((r) => r.data) });
  const [drafts, setDrafts] = useState({});

  const months = data?.months || [];
  const fyOptions = data?.fy_options || ["FY27"];
  const sites = data?.sites || [];

  const saveCell = async (field, month, raw) => {
    const value = raw === "" || raw === null ? 0 : Number(raw);
    try {
      await api.put("/pulled-forward/cell", { field, month, site, value });
      qc.invalidateQueries({ queryKey: ["pf-worksheet"] });
      qc.invalidateQueries({ queryKey: ["site-summary"] });
      qc.invalidateQueries({ queryKey: ["site-worksheet"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const editCell = (field, month, stored, tone) => {
    const key = `${field}:${month}`;
    const val = drafts[key] !== undefined ? drafts[key] : (stored ?? 0);
    return (
      <td key={key} className="px-1.5 py-1.5 text-right">
        <input
          type="number" step="0.01"
          data-testid={`pf-${field}-${month}`}
          value={val}
          onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
          onBlur={(e) => {
            const raw = e.target.value;
            if (String(stored ?? 0) !== String(raw === "" ? 0 : Number(raw))) saveCell(field, month, raw);
            setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
          }}
          className={`w-24 text-right rounded border border-slate-200 dark:border-slate-700 bg-transparent px-1.5 py-0.5 text-xs font-mono-num outline-none focus:ring-1 focus:ring-blue-400 ${tone || ""}`}
        />
      </td>
    );
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <p className="text-sm text-slate-500 max-w-2xl">Running balance tracker. Enter the <strong>Pulled Forward Saving</strong> and <strong>Savings in period</strong> each month — the <strong>Closing Balance</strong> rolls forward automatically (previous balance + pulled forward − savings). Set the opening balance in the <strong>O/B</strong> column.</p>
        <div className="flex gap-2">
          <Select value={site} onValueChange={setSite}><SelectTrigger className="w-40" data-testid="pf-site-select"><SelectValue /></SelectTrigger><SelectContent className="max-h-64">{sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Select value={fy} onValueChange={setFy}><SelectTrigger className="w-28" data-testid="pf-fy-select"><SelectValue /></SelectTrigger><SelectContent>{fyOptions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      <TableShell>
        <table className="text-sm border-collapse" data-testid="pulled-forward-matrix">
          <thead>
            <tr className="bg-slate-800 text-white text-xs">
              <th className="sticky left-0 z-20 bg-slate-800 px-3 py-2.5 text-left min-w-[180px]">{site}</th>
              <th className="px-3 py-2.5 text-right min-w-[92px]">O/B</th>
              {months.map((m) => <th key={m} className="px-3 py-2.5 text-right min-w-[92px] whitespace-nowrap">{m}</th>)}
              <th className="px-3 py-2.5 text-right bg-slate-900 min-w-[100px]">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="text-slate-500 border-t border-slate-100 dark:border-slate-800">
              <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-3 py-2">Weeks in period</td>
              <td />
              {months.map((m, i) => <td key={m} className="px-3 py-2 text-right font-mono-num">{data.weeks[i]}</td>)}
              <td className="px-3 py-2 text-right font-mono-num bg-slate-50 dark:bg-slate-800/40">{data.weeks.reduce((a, b) => a + b, 0)}</td>
            </tr>
            <tr className="border-t border-slate-100 dark:border-slate-800">
              <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-3 py-2 font-medium text-blue-700 dark:text-blue-400">Pulled Forward Saving</td>
              {editCell("opening", "O/B", data.opening_balance)}
              {months.map((m, i) => editCell("pulled_forward", m, data.pulled_forward[i], "text-blue-600"))}
              <td className="px-3 py-2 text-right font-mono-num font-semibold text-blue-700 bg-slate-50 dark:bg-slate-800/40">{gbp(data.pulled_forward_total)}</td>
            </tr>
            <tr className="border-t border-slate-100 dark:border-slate-800">
              <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-3 py-2 font-medium text-emerald-700 dark:text-emerald-400">Savings in period</td>
              <td className="px-3 py-2 text-right font-mono-num text-slate-300">{gbp(0)}</td>
              {months.map((m, i) => editCell("savings_in_period", m, data.savings_in_period[i], "text-emerald-600"))}
              <td className="px-3 py-2 text-right font-mono-num font-semibold text-emerald-700 bg-slate-50 dark:bg-slate-800/40">{gbp(data.savings_total)}</td>
            </tr>
            <tr className="border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 font-semibold">
              <td className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-800/60 px-3 py-2">Closing Balance</td>
              <td className="px-3 py-2 text-right font-mono-num">{gbp(data.opening_balance)}</td>
              {months.map((m, i) => <td key={m} className="px-3 py-2 text-right font-mono-num" data-testid={`pf-closing-${m}`}>{gbp(data.closing_balance[i])}</td>)}
              <td className="px-3 py-2 text-right font-mono-num bg-slate-100 dark:bg-slate-900" data-testid="pf-closing-final">{gbp(data.closing_final)}</td>
            </tr>
          </tbody>
        </table>
      </TableShell>
      <p className="text-xs text-slate-400 mt-2">Cells save automatically when you leave them. Balances feed each site's carried-forward savings.</p>
    </div>
  );
}

function CreditsTab() {
  const qc = useQueryClient();
  const [fy, setFy] = useState("FY27");
  const { data, isLoading } = useQuery({ queryKey: ["sc-matrix", fy], queryFn: () => api.get("/service-credits/matrix", { params: { fy } }).then((r) => r.data) });
  const [drafts, setDrafts] = useState({});

  const months = data?.months || [];
  const rows = data?.rows || [];
  const fyOptions = data?.fy_options || ["FY27"];

  const saveScore = async (rowId, month, raw) => {
    const val = raw === "" || raw === null ? null : Number(raw);
    try {
      await api.put(`/service-credits/${rowId}/score`, { month, score: val });
      qc.invalidateQueries({ queryKey: ["sc-matrix"] });
      qc.invalidateQueries({ queryKey: ["reconciliation"] });
      qc.invalidateQueries({ queryKey: ["site-worksheet"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <p className="text-sm text-slate-500 max-w-2xl">Enter each month's compliance <strong>Score</strong> (%). When a score falls <strong>below</strong> the standard's target, its monthly credit becomes due automatically and feeds the Reconciliation and each site's worksheet.</p>
        <Select value={fy} onValueChange={setFy}>
          <SelectTrigger className="w-32" data-testid="sc-fy-select"><SelectValue /></SelectTrigger>
          <SelectContent>{fyOptions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <TableShell>
        <table className="text-sm border-collapse" data-testid="service-credits-matrix">
          <thead>
            <tr className="bg-slate-800 text-white text-xs">
              <th className="sticky left-0 z-20 bg-slate-800 px-3 py-2.5 text-left min-w-[210px]">Service Standard</th>
              <th className="px-2 py-2.5 text-right whitespace-nowrap">Target %</th>
              <th className="px-2 py-2.5 text-right whitespace-nowrap">Monthly £</th>
              {months.map((m) => <th key={m} className="px-2 py-2.5 text-center min-w-[86px] whitespace-nowrap">{m}</th>)}
              <th className="px-3 py-2.5 text-right bg-slate-900 min-w-[92px]">Total Due</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`sc-row-${r.number}`}>
                <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-3 py-2 whitespace-nowrap">
                  <span className="font-medium text-slate-800 dark:text-slate-200">{r.number}. {r.service_standard}</span>
                  <span className="block text-[11px] text-slate-400">{r.recorded_by}</span>
                </td>
                <td className="px-2 py-2 text-right font-mono-num text-slate-500">{num(r.target, 1)}%</td>
                <td className="px-2 py-2 text-right font-mono-num">{gbp(r.monthly_credit)}</td>
                {months.map((m) => {
                  const key = `${r.id}:${m}`;
                  const stored = r.scores[m] ?? "";
                  const val = drafts[key] !== undefined ? drafts[key] : stored;
                  const due = r.credits[m];
                  return (
                    <td key={m} className="px-1.5 py-1.5 text-center align-top">
                      <input
                        type="number" step="0.1"
                        data-testid={`sc-score-${r.number}-${m}`}
                        value={val}
                        onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                        onBlur={(e) => {
                          const raw = e.target.value;
                          const changed = String(stored) !== String(raw);
                          if (changed) saveScore(r.id, m, raw);
                          setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
                        }}
                        className={`w-16 text-center rounded border px-1 py-0.5 text-xs font-mono-num outline-none focus:ring-1 focus:ring-blue-400 ${due > 0 ? "border-rose-300 bg-rose-50 dark:bg-rose-950/30" : "border-slate-200 dark:border-slate-700 bg-transparent"}`}
                      />
                      <span className={`block text-[10px] mt-0.5 font-mono-num ${due > 0 ? "text-rose-600 font-semibold" : "text-slate-300"}`}>{due > 0 ? gbp(due) : "£0"}</span>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right font-mono-num font-semibold bg-slate-50 dark:bg-slate-800/40" data-testid={`sc-rowtotal-${r.number}`}>{gbp(r.total_due)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100 dark:bg-slate-800 font-semibold">
              <td className="sticky left-0 z-10 bg-slate-100 dark:bg-slate-800 px-3 py-2.5">Monthly credit due</td>
              <td /><td />
              {months.map((m) => <td key={m} className="px-2 py-2.5 text-center font-mono-num text-rose-700" data-testid={`sc-total-${m}`}>{gbp(data.monthly_totals[m])}</td>)}
              <td className="px-3 py-2.5 text-right font-mono-num text-rose-700 bg-slate-200 dark:bg-slate-900" data-testid="sc-grand-total">{gbp(data.grand_total)}</td>
            </tr>
          </tfoot>
        </table>
        {rows.length === 0 && <EmptyState message="No service standards found." />}
      </TableShell>
      <p className="text-xs text-slate-400 mt-2">Scores save automatically when you leave a cell. Cells turn red when a credit is due.</p>
    </div>
  );
}
